import { AbstractCreateEntityComponent } from '@abstracts/entities/create-entity';
import { DecimalPipe } from '@angular/common';
import { Component, EventEmitter, Inject, Injector, Input, OnInit, Output } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ChargingParkSubscriptionDto } from '@dto/chargingParkSubscriptionDto';
import { InvoiceSettingsDto } from '@dto/invoiceSettingsDto';
import { PricingDto } from '@dto/pricingDto';
import { UserDto } from '@dto/userDto';
import { BillingMethodEnum } from '@enums/billingMethodEnum.enum';
import { IEntity } from '@interfaces/entity';
import { Pricing } from '@models/pricing';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { CustomersService } from '@services/entity/customers.service';
import { InvoicesService } from '@services/entity/invoices.service';
import { PricingsService } from '@services/entity/pricings.service';
import { StripeInfosService } from '@services/entity/stripe-infos.service';
import { UsersService } from '@services/entity/users.service';
import { ToastService } from '@services/toast/toast.service';
import { plainToClass } from 'class-transformer';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-create-pricing',
  templateUrl: './create-pricing.component.html',
  styleUrls: ['./create-pricing.component.scss'],
})
export class CreatePricingComponent extends AbstractCreateEntityComponent implements OnInit {

  public className = 'CreatePricingComponent'

  @Input() customerId: number;
  @Output() newPricingEvent = new EventEmitter<IEntity>();

  private ownerUserId: string
  public users?: UserDto[] = []
  public selectedUsers?: UserDto[] = []
  public availableUsers?: UserDto[] = []
  public usersSelectEmpty: string = 'Keine Benutzer vorhanden'
  public pricings?: PricingDto[] = []
  public hasStripeAccount: boolean = false
  public stripeProductId: string
  public pricingType: string
  public invoiceSettings: InvoiceSettingsDto

  public isUser: boolean = false;
  public isCustomer: boolean = false;
  public isOperator: boolean = false;

  compareUsersById(a: UserDto, b: UserDto): boolean {
    return a?.id === b?.id;
  }

  userMultiSelectSearchFn = (term: string, item: UserDto): boolean => {
    const selectedUsers = this.formService?.form?.get('selectedUsers')?.value as UserDto[] | undefined;
    if (selectedUsers?.some(u => u.id === item.id)) {
      return false;
    }
    const search = term.toLowerCase();
    return (
      (item.fullName?.toLowerCase().includes(search) ?? false) ||
      (item.email?.toLowerCase().includes(search) ?? false)
    );
  };

  clear(item: UserDto): void {
    const control = this.formService.form.get('selectedUsers');
    if (!control) {
      return;
    }
    const currentValues = (control.value || []) as UserDto[];
    control.setValue(currentValues.filter(u => u.id !== item.id));
    control.markAsDirty();
    control.markAsTouched();
  }

  get assignedUsers(): UserDto[] {
    return (this.formService?.form?.get('selectedUsers')?.value as UserDto[]) ?? [];
  }

  getUserDisplayName(user: UserDto): string {
    const match = this.availableUsers?.find(u => u.id === user.id);
    const email = match?.email ?? user.email;
    return email ? `${user.fullName} (${email})` : user.fullName;
  }

  trackUserById(_index: number, user: UserDto): string {
    return user.id;
  }

  constructor(
    @Inject('BASE_URL') private baseUrl: string,
    private stripeInfosService: StripeInfosService,
    private pricingsService: PricingsService,
    private usersService: UsersService,
    private customersService: CustomersService,
    public activatedRoute: ActivatedRoute,
    public toastService: ToastService,
    public invoicesService: InvoicesService,
    public activeModal: NgbActiveModal,
    injector: Injector
  ) {
    super(pricingsService, injector)
  }

  async ngOnInit() {
    this.getRoles();

    if (this.isOperator) {
      this.ownerUserId = localStorage.getItem('masterUserId')
    } else {
      this.ownerUserId = localStorage.getItem('userId')
    }
    this.pricingType = localStorage.getItem('pricingType')

    super.ngOnInit();
    this.getStripeInfo();
    this.getPricings();
  }

  private getStripeInfo() {
    if (!this.ownerUserId) return
    this.stripeInfosService.getStripeInfoOfUser(this.ownerUserId).subscribe(info => {
      this.hasStripeAccount = info.stripeAccountId != null
      this.stripeProductId = info.stripeProductId
    }, error => { })
  }

  private getPricings() {
    if (!this.ownerUserId) return
    this.pricingsService.getPricingsOfUser(this.ownerUserId).subscribe(result => {
      this.pricings = plainToClass(PricingDto, result)
      switch (this.pricingType) {
        case 'Rechnung':
          this.pricings = this.getInvoicePricings();
          this.loadUsers()
          break
        case 'Abonnement':
          this.pricings = this.getAbonnementPricings();
          this.loadUsers()
          break
        case 'QR-Code':
          this.pricings = this.getQRCodePricings();
          break
      }
    }, error => { })
  }

  parsePrice() {
    let price = this.formService.form.get('price').value.toString()
    if (price.split('.')[1].length < 2) price += '0'
    const parsedPrice = price.match(/[0-9]{1,2}\.[0-9]{2}/g)[0]
    this.formService.form.get('price').setValue(parsedPrice);
  }

  createForm() {
    this.formService.validators = {
      name: [Validators.required],
      description: [],
      price: [Validators.required],
      priceDc: [Validators.required],
      pricing: [],
      pricingToReplace: [],
      archivePricingToReplace: [],
      selectedUsers: [],
    }

    this.formService.form = new UntypedFormGroup({
      name: new UntypedFormControl('', this.formService.validators['name']),
      description: new UntypedFormControl('', this.formService.validators['description']),
      price: new UntypedFormControl('', this.formService.validators['price']),
      priceDc: new UntypedFormControl('', this.formService.validators['priceDc']),
      pricing: new UntypedFormControl('', this.formService.validators['pricing']),
      pricingToReplace: new UntypedFormControl(null, this.formService.validators['pricingToReplace']),
      archivePricingToReplace: new UntypedFormControl(false, this.formService.validators['archivePricingToReplace']),
      selectedUsers: new UntypedFormControl(this.selectedUsers, this.formService.validators['selectedUsers']),
    })
  }

  async loadUsers() {
    const users = this.isOperator
      ? await this.usersService.getUsersOfCustomerAsync(this.customerId)
      : await this.usersService.getUsersOfCurrentCustomerAsync();
    switch (this.pricingType) {
      case 'Rechnung':
        this.availableUsers = users.filter(u => u.pricingId == null)
        break
      case 'Abonnement':
        this.availableUsers = users.filter(u => u.pricingId == null && u.email != null && u.email.length > 0)
        break
    }

    if (users.length > 0 && this.availableUsers.length == 0) {
      this.usersSelectEmpty = "Alle Benutzer haben bereits einen Tarif";
    }

    this.availableUsers.sort((a, b) => a.fullName.localeCompare(b.fullName));

    this.createForm();
  }

  public updatedPricingToReplace() {
    const pricingToReplace = this.formService.form.get('pricingToReplace').value
    const pricingAffected = this.pricings.find(p => p.id == pricingToReplace)
    if (pricingAffected) {
      const usersOfPricingToReplace = pricingAffected.selectedUsers || []
      this.availableUsers = this.users.filter(u => u.pricingId == null)
      this.availableUsers.concat(usersOfPricingToReplace)
      const usersSelected = this.formService.form.get('selectedUsers').value.filter(u => u.pricingId == null)
      this.formService.form.patchValue({ selectedUsers: usersSelected.concat(usersOfPricingToReplace) })
    } else {
      this.availableUsers = this.users.filter(u => u.pricingId == null)
      const usersSelected = this.formService.form.get('selectedUsers').value.filter(u => u.pricingId == null)
      this.formService.form.patchValue({ selectedUsers: usersSelected })
    }
  }

  swalFireInvalitePrice() {
    Swal.fire({
      title: $localize`:@@InviteUserSuccessHeader:Tarifpreis € 0,00 ungültig`,
      text: $localize`:@@InviteUserSuccessBody:Der Preis für eine kWh muss mindestens € 0,01 betragen. Um einen Benutzer kostenlos laden zu lassen, weisen Sie ihm keinen Tarif zu.`,
      icon: 'info',
      confirmButtonText: $localize`:@@DialogOkButton:OK`,
      confirmButtonColor: 'green',
    });
  }

  onSubmit(formValue) {

    if (formValue.price <= 0 && this.pricingType == 'QR-Code') {
      this.swalFireInvalitePrice();
      return;
    }


    let type = this.pricingType
    let toastPrefix = type == 'Rechnung' ? 'Rechnungs' : type

    this.toastService.loading($localize`:@@CreatingPricingToast:Tarif wird erstellt.`);

    let pricing = new PricingDto(formValue)
    pricing.price = formValue.price / 100
    pricing.priceDc = formValue.priceDc / 100
    pricing.created = new Date()
    if (this.isOperator) {
      pricing.ownerUserId = localStorage.getItem('masterUserId');

    } else {
      pricing.ownerUserId = localStorage.getItem('userId');
    }

    if (type == 'Rechnung') {
      pricing.billingMethod = BillingMethodEnum.Manual
    }
    if (type == 'Abonnement') {
      pricing.billingMethod = BillingMethodEnum.Automatic
    }
    if (type == 'QR-Code') {
      pricing.billingMethod = BillingMethodEnum.QRCode
    }

    // Create this new pricing
    this.pricingsService.createPricing(pricing).subscribe(newPricing => {
      let pricingToReplace = this.pricings.find(p => p.id == formValue.pricingToReplace)
      this.pricingsService.emitCreated(newPricing, pricingToReplace)
      this.toastService.success($localize`:@@CreatingPricingSuccessToast:Tarif erfolgreich erstellt.`, 0, true);
      this.activeModal.close()
    }, error => {
      if (error.message.includes('active subscription')) {
        this.toastService.error($localize`:@@RemoveActivePricingErrorToast:Benutzer mit aktivem Abonnement müssen den Tarif benutzerseitig kündigen. Sie können dem Benutzer aber die Zugriffsrechte an den Ladestationen entziehen.`, 0, true);
      } else {
        this.toastService.error($localize`:@@CreatingPricingErrorToast:Tarif konnte nicht erstellt werden.`, 0, true);
      }
    })

    localStorage.removeItem('pricingType')
  }

  private getRoles() {
    if (this.authenticationService.userHasRole('User')) {
      this.isUser = true;
    }

    if (this.authenticationService.userHasRole('Customer')) {
      this.isCustomer = true;
    }

    if (this.authenticationService.userHasRole('Operator')) {
      this.isOperator = true;
    }
  }

  public getInvoicePricings() { return this.pricings.filter(p => this.isInvoicePricing(p)) }
  public getAbonnementPricings() { return this.pricings.filter(p => this.isAbonnementPricing(p)) }
  public getQRCodePricings() { return this.pricings.filter(p => this.isQRCodePricing(p)) }

  public isInvoicePricing(p: PricingDto) { return p.billingMethod == BillingMethodEnum.Manual }
  public isAbonnementPricing(p: PricingDto) { return p.billingMethod == BillingMethodEnum.Automatic }
  public isQRCodePricing(p: PricingDto) { return p.billingMethod == BillingMethodEnum.QRCode }

}
